import React, { useEffect, useState, useMemo } from 'react';
import { Modal, Box, Typography, Button, TextField, Radio, FormControlLabel, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import { useForm } from 'react-hook-form';
import { FormInputDropdown } from '../../form/FormInputDropdown';
import QuestionMarkIcon from '../../../_icons/QuestionMarkIcon';
import { getGolfDefaultSettingsByAreaId, updateGolfDefaultSettingsByAreaId } from '../../../api/golf';
import { useSelector } from 'react-redux';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from 'use-toast-mui';

const GolfSpotBasicSettingModal = ({ open, handleClose, spotData }) => {
  const { t } = useTranslation();
  const user = useSelector(state => state.user.user);
  const toast = useToast();
  const queryClient = useQueryClient();
  const branchId = user.branch?.branchId;
  const { control, setValue, watch, getValues } = useForm({
    defaultValues: {
      bufferMinute: '0',
      usageMinute: '30',
      cancelType: 'BEFORE_START',
      cancelCustomDays: '0',
      cancelCustomHours: '0',
      cancelCustomMinutes: '0',
      absenceProcessingType: 'AUTO',
      ticketDeductionType: 'DEDUCT',
    },
  });

  const [bufferMinute, setBufferMinute] = useState('0');
  const [customBufferMinute, setCustomBufferMinute] = useState('');
  const [usageMinute, setUsageMinute] = useState('30');
  const [customUsageMinute, setCustomUsageMinute] = useState('');
  const [cancelType, setCancelType] = useState('BEFORE_START');
  const [cancelCustomDays, setCancelCustomDays] = useState('0');
  const [cancelCustomHours, setCancelCustomHours] = useState('');
  const [cancelCustomMinutes, setCancelCustomMinutes] = useState('');
  const [absenceProcessingType, setAbsenceProcessingType] = useState('AUTO');
  const [ticketDeductionType, setTicketDeductionType] = useState('DEDUCT');

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '995px',
    maxWidth: '95vw',
    maxHeight: '95vh',
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: '8px',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  };

  // 기본 옵션 조회 - 모달이 열릴 때마다 데이터 다시 조회
  const { data, refetch } = useQuery({
    queryKey: ['golfSpotBasicSetting', user?.token, spotData?.golfAreaId],
    queryFn: () => getGolfDefaultSettingsByAreaId(user?.token, spotData?.golfAreaId),
    enabled: !!user?.token && !!spotData?.golfAreaId && open,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

  // 모달이 열릴 때마다 데이터 다시 조회
  useEffect(() => {
    if (open && user?.token && spotData?.golfAreaId) {
      refetch();
    }
  }, [open, user?.token, spotData?.golfAreaId, refetch]);

  // 기본옵션 정보를 가져온 data를 기반으로 초기값 계산
  const initialValues = useMemo(() => {
    if (!data) return null;

    // bufferMinute 처리
    let prepTime = '0';
    let customPrepTime = '';
    if (data.bufferMinute !== undefined) {
      const bufferValue = String(data.bufferMinute);
      if (['0', '1', '3', '5', '10', '15'].includes(bufferValue)) {
        prepTime = bufferValue;
      } else {
        prepTime = 'CUSTOM';
        customPrepTime = bufferValue;
      }
    }

    // usageMinute 처리 - 0이면 custom으로 설정
    let useTime = '30';
    let customUseTime = '';
    if (data.usageMinute !== undefined) {
      const usageValue = String(data.usageMinute);
      if (usageValue === '0') {
        useTime = 'CUSTOM';
        customUseTime = '0';
      } else if (['30', '45', '60', '90', '120'].includes(usageValue)) {
        useTime = usageValue;
      } else {
        useTime = 'CUSTOM';
        customUseTime = usageValue;
      }
    }

    // cancelType 처리
    let cancelTime = 'BEFORE_START';
    let customCancelDays = '0';
    let customCancelHours = '';
    let customCancelMinutes = '';
    // API 는 직접 설정이 아니면 cancelCustom* 을 null 로 준다. null 을 "직접 설정"으로 오인하면
    // 안 건드리고 저장해도 CANCEL_TYPE=CUSTOM, DAYS=NULL 로 덮어쓴다 — undefined 와 null 을 같이 걸러야 한다.
    const hasCustomCancellation =
      (data.cancelCustomDays != null && data.cancelCustomDays !== 0) ||
      (data.cancelCustomHours != null && data.cancelCustomHours !== 0) ||
      (data.cancelCustomMinutes != null && data.cancelCustomMinutes !== 0);

    if (hasCustomCancellation) {
      cancelTime = 'CUSTOM';
      if (data.cancelCustomDays != null) {
        customCancelDays = String(data.cancelCustomDays);
      }
      if (data.cancelCustomHours != null) {
        customCancelHours = String(data.cancelCustomHours);
      }
      if (data.cancelCustomMinutes != null) {
        customCancelMinutes = String(data.cancelCustomMinutes);
      }
    } else if (data.cancelType) {
      // API에서 오는 값 그대로 사용 (BEFORE_START, NO_CANCEL_AFTER_RESERVATION, NO_SAME_DAY_CANCEL)
      cancelTime = data.cancelType;
    }

    // absenceProcessingType 처리 - API 값 그대로 사용
    let absenceProc = 'AUTO';
    if (data.absenceProcessingType) {
      absenceProc = data.absenceProcessingType;
    }

    // ticketDeductionType 처리 - API 값 그대로 사용
    let ticketDeduct = 'DEDUCT';
    if (data.ticketDeductionType) {
      ticketDeduct = data.ticketDeductionType;
    }

    return {
      prepTime,
      customPrepTime,
      useTime,
      customUseTime,
      cancelTime,
      customCancelDays,
      customCancelHours,
      customCancelMinutes,
      absenceProc,
      ticketDeduct,
    };
  }, [data]);

  // initialValues가 계산되면 state 업데이트
  useEffect(() => {
    if (initialValues && open) {
      setBufferMinute(initialValues.prepTime);
      setCustomBufferMinute(initialValues.customPrepTime);
      setUsageMinute(initialValues.useTime);
      setCustomUsageMinute(initialValues.customUseTime);
      setCancelType(initialValues.cancelTime);
      setCancelCustomDays(initialValues.customCancelDays);
      setCancelCustomHours(initialValues.customCancelHours);
      setCancelCustomMinutes(initialValues.customCancelMinutes);
      setAbsenceProcessingType(initialValues.absenceProc);
      setTicketDeductionType(initialValues.ticketDeduct);

      // react-hook-form 값도 업데이트
      if (initialValues.customCancelHours !== undefined && initialValues.customCancelHours !== '') {
        setValue('cancelCustomHours', Number(initialValues.customCancelHours));
      }
      if (initialValues.cancelCustomMinutes !== undefined && initialValues.cancelCustomMinutes !== '') {
        setValue('cancelCustomMinutes', Number(initialValues.cancelCustomMinutes));
      }
    }
  }, [initialValues, open, setValue]);

  // 기본 설정 수정
  const mutation = useMutation({
    mutationFn: submitData => updateGolfDefaultSettingsByAreaId(user?.token, spotData?.golfAreaId, submitData),
    onSuccess: () => {
      toast.success('기본 설정이 수정되었습니다.');
      // 기본 설정 쿼리 최신화 (GolfSpotDetailModal과 GolfSpotBasicSettingModal 모두)
      if (spotData?.golfAreaId) {
        queryClient.invalidateQueries({ queryKey: ['golfSpotBasicSetting', user?.token, spotData?.golfAreaId] });
      }
      handleClose();
    },
    onError: error => {
      toast.error('오류가 발생했습니다.');
    },
  });

  const handleSubmit = () => {
    const formValues = getValues();

    // bufferMinute 처리 - CUSTOM이면 customBufferMinute 값 사용
    const finalBufferMinute = bufferMinute === 'CUSTOM' ? customBufferMinute : bufferMinute;

    // usageMinute 처리 - CUSTOM이면 customUsageMinute 값 사용
    const finalUsageMinute = usageMinute === 'CUSTOM' ? customUsageMinute : usageMinute;

    // cancelType이 CUSTOM이면 cancelCustomDays, cancelCustomHours, cancelCustomMinutes 사용
    const finalCancelType = cancelType === 'CUSTOM' ? 'CUSTOM' : cancelType;

    const submitData = {
      bufferMinute: Number(finalBufferMinute),
      usageMinute: Number(finalUsageMinute),
      cancelType: finalCancelType,
      cancelCustomDays: cancelType === 'CUSTOM' ? Number(cancelCustomDays) : 0,
      cancelCustomHours: cancelType === 'CUSTOM' ? Number(formValues.cancelCustomHours || 0) : 0,
      cancelCustomMinutes: cancelType === 'CUSTOM' ? Number(formValues.cancelCustomMinutes || 0) : 0,
      absenceProcessingType: absenceProcessingType,
      ticketDeductionType: ticketDeductionType,
    };
    mutation.mutate(submitData);
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      disableEscapeKeyDown
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description">
      <Box sx={modalStyle}>
        {/* 헤더 */}
        <Box
          sx={{
            height: '56px',
            backgroundColor: '#262626',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: '32px',
          }}>
          <Typography sx={{ fontSize: '20px', fontWeight: '600', color: '#fff' }}>
            {t('Spot basic settings')}
          </Typography>
          <CloseIcon sx={{ color: '#fff', width: '32px', height: '32px', cursor: 'pointer' }} onClick={handleClose} />
        </Box>

        {/* 컨텐츠 */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: '32px',
            display: 'flex',
            flexDirection: 'column',
            gap: '32px',
          }}>
          {/* 설정 옵션들 */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '931px' }}>
            {/* 준비 시간 */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>
                  {t('Preparation time')}
                </Typography>
                <Box
                  sx={{
                    width: '20px',
                    height: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                  <QuestionMarkIcon />
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: '16px', alignItems: 'center', height: '40px' }}>
                <FormControlLabel
                  control={
                    <Radio
                      checked={bufferMinute === '0'}
                      onChange={() => setBufferMinute('0')}
                      sx={{
                        color: '#D9D9D9',
                        '&.Mui-checked': {
                          color: '#0F7F85',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>
                      {t('Start immediately')} (0{t('minutes')})
                    </Typography>
                  }
                />
                <FormControlLabel
                  control={
                    <Radio
                      checked={bufferMinute === '1'}
                      onChange={() => setBufferMinute('1')}
                      sx={{
                        color: '#D9D9D9',
                        '&.Mui-checked': {
                          color: '#0F7F85',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>1{t('minutes')}</Typography>
                  }
                />
                <FormControlLabel
                  control={
                    <Radio
                      checked={bufferMinute === '3'}
                      onChange={() => setBufferMinute('3')}
                      sx={{
                        color: '#D9D9D9',
                        '&.Mui-checked': {
                          color: '#0F7F85',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>3{t('minutes')}</Typography>
                  }
                />
                <FormControlLabel
                  control={
                    <Radio
                      checked={bufferMinute === '5'}
                      onChange={() => setBufferMinute('5')}
                      sx={{
                        color: '#D9D9D9',
                        '&.Mui-checked': {
                          color: '#0F7F85',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>5{t('minutes')}</Typography>
                  }
                />
                <FormControlLabel
                  control={
                    <Radio
                      checked={bufferMinute === '10'}
                      onChange={() => setBufferMinute('10')}
                      sx={{
                        color: '#D9D9D9',
                        '&.Mui-checked': {
                          color: '#0F7F85',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>
                      10{t('minutes')}
                    </Typography>
                  }
                />
                <FormControlLabel
                  control={
                    <Radio
                      checked={bufferMinute === '15'}
                      onChange={() => setBufferMinute('15')}
                      sx={{
                        color: '#D9D9D9',
                        '&.Mui-checked': {
                          color: '#0F7F85',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>
                      15{t('minutes')}
                    </Typography>
                  }
                />
                <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Radio
                        checked={bufferMinute === 'CUSTOM'}
                        onChange={() => setBufferMinute('CUSTOM')}
                        sx={{
                          color: '#D9D9D9',
                          '&.Mui-checked': {
                            color: '#0F7F85',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>
                        {t('Custom setting')}
                      </Typography>
                    }
                  />
                  {bufferMinute === 'CUSTOM' && (
                    <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center', height: '40px' }}>
                      <TextField
                        value={customBufferMinute}
                        onChange={e => setCustomBufferMinute(e.target.value)}
                        placeholder={t('Please enter')}
                        sx={{
                          width: '120px',
                          '& .MuiInputBase-root': {
                            backgroundColor: '#F2F2F2',
                            height: '40px',
                          },
                        }}
                      />
                      <Typography sx={{ fontSize: '16px', color: '#A6A6A6' }}>{t('minutes')}</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>

            {/* 이용 시간 */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>{t('Usage time')}</Typography>
                <Box
                  sx={{
                    width: '20px',
                    height: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                  <QuestionMarkIcon />
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: '16px', alignItems: 'center', height: '40px' }}>
                <FormControlLabel
                  control={
                    <Radio
                      checked={usageMinute === '30'}
                      onChange={() => setUsageMinute('30')}
                      sx={{
                        color: '#D9D9D9',
                        '&.Mui-checked': {
                          color: '#0F7F85',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>
                      30{t('minutes')}
                    </Typography>
                  }
                />
                <FormControlLabel
                  control={
                    <Radio
                      checked={usageMinute === '45'}
                      onChange={() => setUsageMinute('45')}
                      sx={{
                        color: '#D9D9D9',
                        '&.Mui-checked': {
                          color: '#0F7F85',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>
                      45{t('minutes')}
                    </Typography>
                  }
                />
                <FormControlLabel
                  control={
                    <Radio
                      checked={usageMinute === '60'}
                      onChange={() => setUsageMinute('60')}
                      sx={{
                        color: '#D9D9D9',
                        '&.Mui-checked': {
                          color: '#0F7F85',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>
                      60{t('minutes')}
                    </Typography>
                  }
                />
                <FormControlLabel
                  control={
                    <Radio
                      checked={usageMinute === '90'}
                      onChange={() => setUsageMinute('90')}
                      sx={{
                        color: '#D9D9D9',
                        '&.Mui-checked': {
                          color: '#0F7F85',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>
                      90{t('minutes')}
                    </Typography>
                  }
                />
                <FormControlLabel
                  control={
                    <Radio
                      checked={usageMinute === '120'}
                      onChange={() => setUsageMinute('120')}
                      sx={{
                        color: '#D9D9D9',
                        '&.Mui-checked': {
                          color: '#0F7F85',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>
                      120{t('minutes')}
                    </Typography>
                  }
                />
                <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Radio
                        checked={usageMinute === 'CUSTOM'}
                        onChange={() => setUsageMinute('CUSTOM')}
                        sx={{
                          color: '#D9D9D9',
                          '&.Mui-checked': {
                            color: '#0F7F85',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>
                        {t('Custom setting')}
                      </Typography>
                    }
                  />
                  {usageMinute === 'CUSTOM' && (
                    <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center', height: '40px' }}>
                      <TextField
                        value={customUsageMinute}
                        onChange={e => setCustomUsageMinute(e.target.value)}
                        placeholder={t('Please enter')}
                        sx={{
                          width: '120px',
                          '& .MuiInputBase-root': {
                            backgroundColor: '#F2F2F2',
                            height: '40px',
                          },
                        }}
                      />
                      <Typography sx={{ fontSize: '16px', color: '#A6A6A6' }}>{t('minutes')}</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>

            {/* 예약 취소 가능 시간 */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>
                  {t('Reservation cancellation time')}
                </Typography>
                <Box
                  sx={{
                    width: '20px',
                    height: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                  <QuestionMarkIcon />
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <Box sx={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <FormControlLabel
                    control={
                      <Radio
                        checked={cancelType === 'BEFORE_START'}
                        onChange={() => setCancelType('BEFORE_START')}
                        sx={{
                          color: '#D9D9D9',
                          '&.Mui-checked': {
                            color: '#0F7F85',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>
                        {t('Before start time')}
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Radio
                        checked={cancelType === 'NO_CANCEL_AFTER_RESERVATION'}
                        onChange={() => setCancelType('NO_CANCEL_AFTER_RESERVATION')}
                        sx={{
                          color: '#D9D9D9',
                          '&.Mui-checked': {
                            color: '#0F7F85',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>
                        {t('No cancellation after reservation')}
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Radio
                        checked={cancelType === 'NO_SAME_DAY_CANCEL'}
                        onChange={() => setCancelType('NO_SAME_DAY_CANCEL')}
                        sx={{
                          color: '#D9D9D9',
                          '&.Mui-checked': {
                            color: '#0F7F85',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>
                        {t('No same-day cancellation')}
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Radio
                        checked={cancelType === 'CUSTOM'}
                        onChange={() => setCancelType('CUSTOM')}
                        sx={{
                          color: '#D9D9D9',
                          '&.Mui-checked': {
                            color: '#0F7F85',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>
                        {t('Custom setting')}
                      </Typography>
                    }
                  />
                </Box>
                {cancelType === 'CUSTOM' && (
                  <Box sx={{ display: 'flex', gap: '16px', alignItems: 'center', height: '40px', pl: '28px' }}>
                    <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <TextField
                        type="number"
                        value={cancelCustomDays}
                        onChange={e => setCancelCustomDays(e.target.value)}
                        sx={{
                          width: '100px',
                          '& .MuiInputBase-root': {
                            backgroundColor: '#F2F2F2',
                            height: '40px',
                          },
                        }}
                        InputProps={{
                          endAdornment: <QuestionMarkIcon />,
                        }}
                      />
                      <Typography sx={{ fontSize: '16px', color: '#A6A6A6' }}>{t('days')}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <FormInputDropdown
                        control={control}
                        name="cancelCustomHours"
                        sx={{ width: '100px' }}
                        options={Array.from({ length: 24 }, (_, i) => ({ value: i, label: `${i}` }))}
                        disabledItem={<MenuItem disabled>{t('hours')}</MenuItem>}
                      />
                      <Typography sx={{ fontSize: '16px', color: '#A6A6A6' }}>{t('hours')}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <FormInputDropdown
                        control={control}
                        name="cancelCustomMinutes"
                        sx={{ width: '100px' }}
                        options={Array.from({ length: 60 }, (_, i) => ({ value: i, label: `${i}` }))}
                        disabledItem={<MenuItem disabled>{t('minutes')}</MenuItem>}
                      />
                      <Typography sx={{ fontSize: '16px', color: '#A6A6A6' }}>{t('minutes')}</Typography>
                    </Box>
                    <Typography sx={{ fontSize: '16px', color: '#A6A6A6' }}>{t('before')}</Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* 결석 처리 기준 & 결석 시 수강권 차감 여부 */}
            <Box sx={{ display: 'flex', gap: '24px' }}>
              {/* 결석 처리 기준 */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>
                    {t('Absence processing standard')}
                  </Typography>
                  <Box
                    sx={{
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                    <QuestionMarkIcon />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: '16px', alignItems: 'center', height: '40px' }}>
                  <FormControlLabel
                    control={
                      <Radio
                        checked={absenceProcessingType === 'AUTO'}
                        onChange={() => setAbsenceProcessingType('AUTO')}
                        sx={{
                          color: '#D9D9D9',
                          '&.Mui-checked': {
                            color: '#0F7F85',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: '16px', fontWeight: '600', color: '#595959' }}>
                        {t('Automatic absence processing')}
                      </Typography>
                    }
                  />
                </Box>
              </Box>

              {/* 결석 시 수강권 차감 여부 */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>
                    {t('Ticket deduction upon absence')}
                  </Typography>
                  <Box
                    sx={{
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                    <QuestionMarkIcon />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: '16px', alignItems: 'center', height: '40px' }}>
                  <FormControlLabel
                    control={
                      <Radio
                        checked={ticketDeductionType === 'DEDUCT'}
                        onChange={() => setTicketDeductionType('DEDUCT')}
                        sx={{
                          color: '#D9D9D9',
                          '&.Mui-checked': {
                            color: '#0F7F85',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>
                        {t('Deduct ticket')}
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Radio
                        checked={ticketDeductionType === 'NO_DEDUCT'}
                        onChange={() => setTicketDeductionType('NO_DEDUCT')}
                        sx={{
                          color: '#D9D9D9',
                          '&.Mui-checked': {
                            color: '#0F7F85',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>
                        {t('Do not deduct ticket')}
                      </Typography>
                    }
                  />
                </Box>
              </Box>
            </Box>
          </Box>

          {/* 하단 버튼 */}
          <Box sx={{ display: 'flex', gap: '16px', justifyContent: 'flex-end' }}>
            <Button
              sx={{
                width: '120px',
                height: '48px',
                backgroundColor: '#D9D9D9',
                color: '#737373',
                fontSize: '20px',
                fontWeight: '600',
                borderRadius: '8px',
                '&:hover': { backgroundColor: '#D9D9D9' },
              }}
              onClick={handleClose}>
              {t('Cancel')}
            </Button>
            <Button
              sx={{
                width: '240px',
                height: '48px',
                backgroundColor: '#0F7F85',
                color: '#fff',
                fontSize: '20px',
                fontWeight: '600',
                borderRadius: '8px',
                '&:hover': { backgroundColor: '#0F7F85' },
              }}
              onClick={handleSubmit}>
              {t('Settings complete')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default GolfSpotBasicSettingModal;
